import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

export interface ModalProps {
  /** Controls visibility */
  isOpen: boolean;
  /** Called when user closes via Esc, scrim click, or close button */
  onClose: () => void;
  /** Dialog title — rendered in header with aria-labelledby */
  title: React.ReactNode;
  children?: React.ReactNode;
  /** Optional footer content (actions bar) */
  footer?: React.ReactNode;
  /** When false, Esc does not close the modal (explicit-action close only). Default true. */
  closeOnEsc?: boolean;
  /** When false, clicking the scrim does not close the modal (explicit-action close only). Default true. */
  closeOnOverlayClick?: boolean;
  /**
   * `default` = dialog compacto (max-w-lg). `workbench` = superfície de trabalho
   * quase fullscreen (estilo Jira) para tarefas densas — triagem, revisão em lote.
   * Ambos têm corpo rolável; o dialog nunca estoura o viewport.
   */
  size?: 'default' | 'workbench';
  /**
   * Classe de z-index da scrim (default `z-[1000]`). Passe um valor maior
   * (ex.: `z-[1200]`) quando este modal precisa ficar ACIMA de outro modal já
   * aberto — dois modais com o mesmo z-index sobrepõem pela ordem do DOM, o que
   * não é confiável. Sem portal, o z-index explícito garante a sobreposição.
   */
  overlayZClass?: string;
}

/** Query all focusable elements within a container */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), ' +
        'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  size = 'default',
  overlayZClass = 'z-[1000]',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const titleId = React.useId();

  // ---- Focus management -------------------------------------------------------
  // On open: save previously focused element; move focus into dialog
  // On close: restore focus to previously focused element
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement;
      // Next tick — let dialog render before focusing
      const raf = requestAnimationFrame(() => {
        if (dialogRef.current) {
          const focusable = getFocusableElements(dialogRef.current);
          if (focusable.length > 0) {
            focusable[0].focus();
          } else {
            dialogRef.current.focus();
          }
        }
      });
      return () => cancelAnimationFrame(raf);
    } else {
      previouslyFocusedRef.current?.focus();
    }
  }, [isOpen]);

  // ---- Keyboard listeners (focus trap + Esc) -----------------------------------
  // Trap: Tab / Shift+Tab cycle within the dialog
  // Esc: calls onClose
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen || !dialogRef.current) return;

      if (event.key === 'Escape') {
        if (closeOnEsc) {
          event.preventDefault();
          onClose();
        }
        return;
      }

      if (event.key === 'Tab') {
        const focusable = getFocusableElements(dialogRef.current);
        if (focusable.length === 0) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey) {
          // Shift+Tab: if focus is on first, cycle to last
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else {
          // Tab: if focus is on last, cycle to first
          if (document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // ---- Scrim click handler ----------------------------------------------------
  const handleScrimClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Only close when the scrim itself is clicked, not the dialog inside
      if (closeOnOverlayClick && event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose, closeOnOverlayClick]
  );

  if (!isOpen) return null;

  return (
    // Scrim do design system glass (--snapfy-scrim) com blur leve.
    <div
      className={`fixed inset-0 ${overlayZClass} bg-[var(--snapfy-scrim)] backdrop-blur-[4px] flex items-center justify-center p-4`}
      onClick={handleScrimClick}
      aria-hidden="false"
    >
      {/*
       * Dialog — entrance animation respects prefers-reduced-motion.
       * motion-reduce:transition-none disables the fade/scale entrance when
       * the user has requested reduced motion (DESIGN-CRAFT §1, gate of Phase 4).
       */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={[
          // Surface + border + shape
          // Vidro forte com blur e sombra de modal do DS, raio 24.
          'snapfy-modal rounded-modal',
          // Layout: coluna com corpo rolável — o dialog NUNCA cresce além do viewport.
          'flex flex-col',
          size === 'workbench'
            ? 'w-[min(1280px,calc(100vw-2rem))] h-[calc(100vh-2rem)]'
            : 'max-w-lg w-full max-h-[calc(100vh-2rem)]',
          // Entrance: fade + scale — disabled under prefers-reduced-motion
          'animate-none opacity-100',
          'motion-reduce:transition-none',
          // Keyboard focus ring on the dialog itself (tabIndex={-1} allows programmatic focus)
          'focus-visible:outline-none focus-visible:ring-snapfy-focus',
        ].join(' ')}
      >
        {/* Header — fixo; só o corpo rola */}
        <div
          className={[
            'flex items-start justify-between gap-4 px-6 pt-6 pb-4 shrink-0',
            size === 'workbench' ? 'border-b border-snapfy-border' : '',
          ].join(' ')}
        >
          <h2 id={titleId} className="text-[22px] leading-[1.1] font-semibold tracking-heading text-snapfy-text-primary">
            {title}
          </h2>

          {/*
           * Close button — TOKEN PARITY (W-6): IDENTICAL to Plan A's IconButton recipe.
           * Cross-plan imports are forbidden in wave 1; strings inlined verbatim.
           * Phase-5 follow-up: replace with <IconButton aria-label="Fechar" />.
           */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={[
              // Verbatim Plan A IconButton token strings (W-6 parity)
              // Botão fechar do DS: 34px, raio 10, campo translúcido com borda.
              'w-[34px] h-[34px] shrink-0 rounded-control flex items-center justify-center',
              'bg-snapfy-field border border-snapfy-border-field text-snapfy-text-primary transition-colors duration-150',
              'hover:bg-snapfy-raised',
              'focus-visible:outline-none focus-visible:ring-snapfy-focus',
            ].join(' ')}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Body — rolável; min-h-0 permite o flex encolher */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 text-snapfy-text-secondary text-sm leading-relaxed">
          {children}
        </div>

        {/* Optional footer — fixo abaixo do scroll */}
        {footer && (
          <div className="shrink-0 px-6 py-4 border-t border-snapfy-border flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
